import { create } from 'zustand';
import _ from 'lodash';
import { 
    deleteContact,
    downloadContact
} from '../reducers/contact/resources'; 
import { 
    getContactThreads, 
    addContactTag, 
    deleteContactTag,
    setContactStar,
    getContactModel,
    translateThread as translateThreadApi
} from '../components/inbox-v2/resources';
import { updateContactData, updateContact, updateContactDetails, updateContactMeta, validateContactMobile } from '../reducers/contact-detail/resources';
import { getAllGroups } from '../reducers/campaign/resources';
import { getCompanyGroups, addContactToGroups, removeContactFromGroups, syncContactGroups } from '../reducers/group/resources';
import format from '../utils/format';
import { joinRoom, leaveRoom } from '../io';
import { message } from 'antd';
import session from '../utils/session';

const useContactProfileStore = create((set, get) => ({
    contactId: null,
    contact: null,
    loading: false,
    savingDetails: false,
    savingAdditional: false,
    threads: [],
    loadingThreads: false,
    groups: [],
    companyGroups: [],
    otherPrivateGroupCount: 0,
    loadingGroups: false,
    activeTab: 'details',
    // Every conversation_id seen in this contact's thread pages. A contact can
    // have several conversations (one per botmap: ABT keyword number, 1-1
    // longcode, webchat…) and the backend emits `fetchThreads` to the
    // conversation_id room of the row, so we must sit in all of them.
    conversationRooms: [],
    
    // Actions
    setContactId: (id) => set({ contactId: id }),
    setActiveTab: (tab) => set({ activeTab: tab }),

    fetchContact: async (id) => {
        set({ loading: true, contactId: id });
        try {
            const [contactRes, groupsRes] = await Promise.all([
                getContactModel(id),
                getAllGroups()
            ]);
            
            set({ 
                contact: contactRes.data,
                groups: groupsRes.data,
                loading: false 
            });
            
            // Join socket room
            joinRoom(id);
            
            return contactRes.data;
        } catch (error) {
            console.error(error);
            set({ loading: false });
            message.error('Failed to load contact');
        }
    },

    fetchThreads: async (id, page = 1) => {
        set({ loadingThreads: true });
        try {
            const res = await getContactThreads(id, `page=${page}`);
            set(state => ({
               threads: page === 1 ? res.data : [...state.threads, ...res.data],
               loadingThreads: false
            }));

            // Join the conversation_id room of every conversation in this page so
            // fetchThreads events arrive. The API returns a page oldest-first, so
            // `res.data[0]` may belong to a retired conversation (e.g. a 2024 ABT
            // convo) while live inbounds land on the 1-1 longcode convo — joining
            // only the first row's room silently dropped them.
            const { conversationRooms } = get();
            const newRooms = _.uniq(
                (res.data || []).map((t) => t && t.conversation_id).filter(Boolean),
            ).filter((id) => !conversationRooms.includes(id));
            if (newRooms.length) {
                newRooms.forEach((id) => joinRoom(id));
                set({ conversationRooms: [...conversationRooms, ...newRooms] });
            }

            return res.data.length < 15; // EOL check
        } catch (error) {
            console.error(error);
            set({ loadingThreads: false });
        }
    },
    
    updateContactField: async (field, value) => {
        const { contact } = get();
        // Optimistic update
        set({ contact: { ...contact, [field]: value } });
        
        // This is a simplified update, might need robust diffing like in profile.js if using complex nested fields
        // For now assuming direct updates or adapting the existing update logic
    },

    saveContactDetails: async (values) => {
        const { contactId, contact } = get();
        set({ savingDetails: true });

        // Normalize mobile
        let rawMobile = values.mobile;
        if (typeof rawMobile === 'object' && rawMobile !== null) {
            const { countryCode, areaCode, phoneNumber } = rawMobile;
            rawMobile = `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
        }
        // If PhoneInput returns object but format.mobileCleanup expects string
        let mobileNumber = format.mobileCleanup(rawMobile || ''); 
        if (mobileNumber && !mobileNumber.startsWith('+')) {
            mobileNumber = '+1' + mobileNumber;
        }

        const payload = { ...values, mobile: mobileNumber };

        try {
            const res = await updateContactDetails(contactId, payload);
            if (res.data) {
                set({ contact: { ...contact, ...res.data }, savingDetails: false });
                message.success('Contact details saved successfully');
                return true;
            }
        } catch (error) {
            console.error(error);
            set({ savingDetails: false });
            message.error('Failed to save contact details');
            return false;
        }
    },

    saveContactAdditionalInfo: async (values) => {
        const { contactId, contact } = get();
        set({ savingAdditional: true });

        const { notes, ...rest } = values;
        const payload = {
            notes,
            details: Object.keys(rest).map(key => ({
                key,
                value: rest[key]
            }))
        };

        try {
            const res = await updateContactMeta(contactId, payload);
            if (res.data) {
                set({ contact: { ...contact, ...res.data }, savingAdditional: false });
                message.success('Additional information saved successfully');
                return true;
            }
        } catch (error) {
            console.error(error);
            set({ savingAdditional: false });
            message.error('Failed to save additional information');
            return false;
        }

    },

    validateContactMobile: async (mobile) => {
        const { contactId } = get();
        try {
            const res = await validateContactMobile({ mobile, contactId });
            return res.data; // { isValid, canSend, message }
        } catch (error) {
            console.error(error);
            return { isValid: false, message: 'Validation failed' };
        }
    },

    toggleContactStar: async () => {
        const { contactId, contact } = get();
        try {
            const newStatus = !contact.star;
            // Optimistic update
            set({ contact: { ...contact, star: newStatus } });
            
            await setContactStar(contactId, newStatus);
            message.success(newStatus ? 'Contact starred' : 'Contact unstarred');
        } catch (error) {
            // Revert
            set({ contact: { ...contact, star: !contact.star } });
            message.error('Failed to update star status');
        }
    },

    addTag: async (value) => {
        const { contactId, contact } = get();
        try {
             const res = await addContactTag(contactId, value);
             if (res.statusCode === 200) {
                 // Refresh contact to get updated tags with IDs
                 const updatedContact = await getContactModel(contactId);
                 set({ contact: updatedContact.data });
                 message.success('Tag added');
                 return true;
             }
        } catch (error) {
             message.error('Failed to add tag');
        }
        return false;
    },

    removeTag: async (tagId) => {
         const { contactId, contact } = get();
         try {
             const res = await deleteContactTag(tagId);
             if (res.statusCode === 200) {
                 const newTags = contact.tags.filter(t => t.id !== tagId);
                 set({ contact: { ...contact, tags: newTags } });
                 message.success('Tag removed');
                 return true;
             }
         } catch (error) {
              message.error('Failed to remove tag');
         }
         return false;
    },

    deleteContact: async (id) => {
        try {
            await deleteContact(id);
            return true;
        } catch (error) {
            console.error(error);
            message.error('Failed to delete contact');
            return false;
        }
    },

    downloadContactPDF: async (id) => {
        try {
            const res = await downloadContact(id);
            if (res.data && res.data.url) {
                const link = document.createElement('a');
                link.href = res.data.url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            message.error('Failed to download contact');
            return false;
        }
    },

    // Group management actions
    fetchCompanyGroups: async (companyId, contactId) => {
        set({ loadingGroups: true });
        try {
            const res = await getCompanyGroups(companyId, contactId);
            // api-core already unwraps response.data, so res = { statusCode, data, otherPrivateGroupCount }
            const groups = Array.isArray(res?.data) ? res.data : [];
            set({
                companyGroups: groups,
                otherPrivateGroupCount: res?.otherPrivateGroupCount || 0,
                loadingGroups: false
            });
            return groups;
        } catch (error) {
            console.error(error);
            set({ loadingGroups: false });
            message.error('Failed to load groups');
            return [];
        }
    },

    addToGroup: async (groupId) => {
        const { contactId, contact, companyGroups } = get();
        
        // Optimistic update
        const updatedGroups = companyGroups.map(g => 
            g.id === groupId ? { ...g, isMember: true, contact_count: parseInt(g.contact_count || 0) + 1 } : g
        );
        set({ companyGroups: updatedGroups });
        
        // Update contact's contactgroups for count
        const targetGroup = companyGroups.find(g => g.id === groupId);
        let newContactGroup = null;
        if (targetGroup && contact) {
            newContactGroup = {
                group_id: groupId,
                contact_id: contactId,
                group: { name: targetGroup.name, id: groupId },
                created_at: new Date().toISOString()
            };
            set({ 
                contact: { 
                    ...contact, 
                    contactgroups: [...(contact.contactgroups || []), newContactGroup] 
                } 
            });
        }

        try {
            const res = await addContactToGroups(contactId, [groupId]);
            if (res.statusCode === 200) {
                message.success('Added to group');
                return true;
            }
            throw new Error('Failed');
        } catch (error) {
            console.error(error);
            // Revert on error
            set({ companyGroups: companyGroups });
             if (contact && newContactGroup) {
                 set({ 
                     contact: { 
                         ...contact, 
                         contactgroups: (contact.contactgroups || []).filter(cg => cg.group_id !== groupId) 
                     } 
                 });
             }
            message.error('Failed to add to group');
            return false;
        }
    },

    removeFromGroup: async (groupId) => {
        const { contactId, contact, companyGroups } = get();
        
        // Optimistic update
        const updatedGroups = companyGroups.map(g => 
            g.id === groupId ? { ...g, isMember: false, contact_count: Math.max(0, parseInt(g.contact_count || 0) - 1) } : g
        );
        set({ companyGroups: updatedGroups });

        // Update contact's contactgroups for count
        const originalContactgroups = contact ? [...(contact.contactgroups || [])] : [];
        if (contact) {
            set({ 
                contact: { 
                    ...contact, 
                    contactgroups: (contact.contactgroups || []).filter(cg => cg.group_id !== groupId) 
                } 
            });
        }

        try {
            const res = await removeContactFromGroups(contactId, [groupId]);
            if (res.statusCode === 200) {
                message.success('Removed from group');
                return true;
            }
            throw new Error('Failed');
        } catch (error) {
            console.error(error);
            // Revert on error
            set({ companyGroups: companyGroups });
            if (contact) {
                set({ contact: { ...contact, contactgroups: originalContactgroups } });
            }
            message.error('Failed to remove from group');
            return false;
        }
    },

    syncGroups: async (addGroupIds, removeGroupIds) => {
        const { contactId, contact, companyGroups } = get();
        
        // Optimistic update companyGroups
        const updatedGroups = companyGroups.map(g => {
            if (addGroupIds.includes(g.id)) return { ...g, isMember: true, contact_count: parseInt(g.contact_count || 0) + 1 };
            if (removeGroupIds.includes(g.id)) return { ...g, isMember: false, contact_count: Math.max(0, parseInt(g.contact_count || 0) - 1) };
            return g;
        });
        set({ companyGroups: updatedGroups });

        // Optimistic update contact.contactgroups
        const originalContactgroups = contact ? [...(contact.contactgroups || [])] : [];
        if (contact) {
            let newContactGroups = [...originalContactgroups];
            // Remove
            newContactGroups = newContactGroups.filter(cg => !removeGroupIds.includes(cg.group_id));
            // Add
            addGroupIds.forEach(gid => {
                 const targetGroup = companyGroups.find(g => g.id === gid);
                 if (targetGroup && !newContactGroups.some(cg => cg.group_id === gid)) {
                     newContactGroups.push({
                        group_id: gid,
                        contact_id: contactId,
                        group: { name: targetGroup.name, id: gid },
                        created_at: new Date().toISOString()
                     });
                 }
            });
            set({ contact: { ...contact, contactgroups: newContactGroups } });
        }

        try {
            const res = await syncContactGroups(contactId, addGroupIds, removeGroupIds);
            if (res.statusCode === 200) {
                message.success('Groups updated successfully');
                return true;
            }
            throw new Error('Failed');
        } catch (error) {
            console.error(error);
            // Revert
            set({ companyGroups: companyGroups });
             if (contact) {
                set({ contact: { ...contact, contactgroups: originalContactgroups } });
            }
            message.error('Failed to update groups');
            return false;
        }
    },

    applyContactTagsUpdated: ({ contactId, tags }) => {
        const { contact } = get();
        if (!contact || contact.id !== contactId) return;
        set({ contact: { ...contact, tags: tags || [] } });
    },

    applyContactStarUpdated: ({ contactId, star }) => {
        const { contact } = get();
        if (!contact || contact.id !== contactId) return;
        set({ contact: { ...contact, star: !!star } });
    },

    // ADR-035: contact language detected or set elsewhere (inbox pill, another user)
    applyContactLanguageUpdated: ({ contactId, language, source }) => {
        const { contact } = get();
        if (!contact || contact.id !== contactId) return;
        set({ contact: { ...contact, language: language || null, language_source: source || null } });
    },

    // ADR-035: `thread:translated` — the inbound bubble is pushed via receiveMsg
    // before detection/translation runs, so the translation lands on the row later.
    // Mirrors inbox-store applyThreadTranslation; the socket handler in io.js
    // fans out to both stores.
    applyThreadTranslation: ({ threadId, translation }) => {
        if (!threadId || !translation) return;
        const { threads } = get();
        if (!(threads || []).some((t) => t.id === threadId)) return;
        set({ threads: threads.map((t) => (t.id === threadId ? { ...t, translation } : t)) });
    },

    // On-demand "Translate to <lang>" link in ThreadItem (history / viewer language
    // with no stored rendering). Writes into this store, not the inbox one.
    translateThreadOnDemand: async (threadId, targetLang) => {
        const res = await translateThreadApi(threadId, targetLang ? { target_lang: targetLang } : {});
        const data = (res && res.data) || res || {};
        if (data.translation) get().applyThreadTranslation({ threadId, translation: data.translation });
        return data;
    },

    applyUserAvatarUpdated: ({ userId, avatar_url }) => {
        if (!userId) return;
        const { threads } = get();
        const changed = (threads || []).some(
            (t) =>
                t?.user_id === userId ||
                t?.user?.id === userId ||
                (t?.channelDetails?.type === 'user' && t.channelDetails.id === userId) ||
                (t?.channel_name?.type === 'user' && t.channel_name.id === userId),
        );
        if (!changed) return;
        set({
            threads: threads.map((t) => {
                const matchesUser = t?.user_id === userId || t?.user?.id === userId;
                const matchesChannelDetails = t?.channelDetails?.type === 'user' && t.channelDetails.id === userId;
                const matchesChannelName =
                    t?.channel_name && typeof t.channel_name === 'object'
                        && t.channel_name.type === 'user' && t.channel_name.id === userId;
                if (!matchesUser && !matchesChannelDetails && !matchesChannelName) return t;
                return {
                    ...t,
                    ...(matchesUser && t.user ? { user: { ...t.user, avatar_url } } : {}),
                    ...(matchesChannelDetails ? { channelDetails: { ...t.channelDetails, avatar_url } } : {}),
                    ...(matchesChannelName ? { channel_name: { ...t.channel_name, avatar_url } } : {}),
                };
            }),
        });
    },

    applyContactGroupsUpdated: ({ contactId, groups }) => {
        const { contact, companyGroups } = get();
        const userId = session.get('userData')?.id;
        const visible = (groups || []).filter(g =>
            g.privacy !== 'private' || (userId && g.user_id === userId)
        );

        const next = {};

        if (contact && contact.id === contactId) {
            // GroupsTab reads contactgroups (array of { group_id, group: { id, name } }).
            const contactgroups = visible.map(g => ({
                group_id: g.id,
                contact_id: contactId,
                group: { id: g.id, name: g.name },
            }));
            next.contact = { ...contact, contactgroups };
        }

        // Refresh isMember flags on companyGroups so GroupsTab checkbox state
        // reflects the new membership for the viewing user.
        if (Array.isArray(companyGroups) && companyGroups.length && contact && contact.id === contactId) {
            const memberIds = new Set(visible.map(g => g.id));
            next.companyGroups = companyGroups.map(g => ({ ...g, isMember: memberIds.has(g.id) }));
        }

        if (Object.keys(next).length) set(next);
    },

    // Leave socket rooms (called on unmount)
    leaveRooms: () => {
        const { contactId, conversationRooms } = get();
        if (contactId) leaveRoom(contactId);
        (conversationRooms || []).forEach((id) => leaveRoom(id));
        set({ conversationRooms: [] });
    },

    // Socket handlers
    handleSocketMessage: (data) => {
        const { contactId, threads } = get();

        let threadData = null;
        let incomingContactId = null;

        if (data.thread && data.contact) {
            // receiveMsg payload structure
            // data = { contact: {...}, thread: {...} }
            incomingContactId = data.contact.id;
            threadData = data.thread;
        } else if (data.conversation && data.message) {
             // threadUpdate payload structure
             // data = { conversation: { contact_id: ... }, ...threadProperties }
             incomingContactId = data.conversation.contact_id;
             // Compute channel_name from conversation.botmap (mirrors backend getChannelName)
             let channel_name = undefined;
             if (data.conversation?.botmap) {
                 const botmap = data.conversation.botmap;
                 if (['messagemedia', 'nexmo', 'twilio'].includes(botmap.type)) {
                     if (['longcode', 'b-longcode'].includes(botmap.default)) {
                         channel_name = { type: 'user', id: data.user_id || null, name: data.user?.name || '', avatar_url: data.user?.avatar_url || null };
                     } else if (['broadcast', 'deleted-broadcast'].includes(botmap.default)) {
                         channel_name = { type: 'system', id: null, name: 'Broadcast', avatar_url: null };
                     } else if (botmap.default === null) {
                         channel_name = { type: 'system', id: null, name: 'GoBe', avatar_url: null };
                     }
                 } else if (botmap.type === 'webchat') {
                     channel_name = { type: 'bot', id: null, name: botmap.bot?.name || 'Chat', avatar_url: null };
                 } else if (botmap.type === 'whatsapp') {
                     // ADR-025: mirrors inbox-store — a user-sent WhatsApp reply is
                     // attributed like a longcode text; bot traffic shows the channel.
                     channel_name = data.user?.id
                         ? { type: 'user', id: data.user_id || data.user.id || null, name: data.user?.name || '', avatar_url: data.user?.avatar_url || null }
                         : { type: 'bot', id: null, name: 'WhatsApp', avatar_url: null };
                 }
             }
             // Normalize to match thread structure
             threadData = {
                ...data, // includes message, created_at, etc.
                direction: data.direction || 'outbound',
                id: data.id,
                conversation_id: data.conversation_id,
                channel_name
             };
        } else if (data.isScheduled) {
             // scheduledMessage payload (passed from UserProfile socket listener potentially, or normalized before calling)
              incomingContactId = data.conversation.contact_id;
              threadData = data;
        }

        // Check if message belongs to this contact
        if (incomingContactId === contactId && threadData) {
           // Deduplicate
           const exists = threads.some(t => t.id === threadData.id);
           if (!exists) {
               // Sort on insert so future-dated scheduled bubbles end up below
               // just-now real messages (mirrors inbox-v2 store behaviour).
               const next = _.sortBy([...threads, threadData], (t) => new Date(t.created_at).getTime());
               set({ threads: next });
           } else {
               // Update existing thread (e.g. status change). Merge so fields set
               // by an earlier event (e.g. channelDetails from fetchThreads) are
               // preserved when the next event omits them.
               set({
                   threads: threads.map(t => t.id === threadData.id ? { ...t, ...threadData } : t)
               });
           }
        }
    },

    // Handle fetchThreads socket event (raw thread record with conversation_id)
    handleFetchThreads: (thread) => {
        const { threads, conversationRooms } = get();
        if (!thread || !thread.id) return;
        // Only accept rows from one of this contact's joined conversation rooms
        if (!(conversationRooms || []).includes(thread.conversation_id)) return;
        const exists = threads.some(t => t.id === thread.id);
        if (!exists) {
            const next = _.sortBy([...threads, thread], (t) => new Date(t.created_at).getTime());
            set({ threads: next });
        }
    },

    // ADR-027 scheduled-message bubble events — bubble disappears on any cancel/skip/send/delete.
    applyScheduledMessageSocketEvent: (event, payload) => {
        const { threads, contactId } = get();
        if (
            event === 'scheduled_message:cancelled'
            || event === 'scheduled_message:sent'
            || event === 'scheduled_message:deleted'
        ) {
            set({ threads: _.filter(threads, (t) => t.id !== payload.id) });
        } else if (event === 'scheduled_message:rescheduled' && payload.schedule) {
            set({
                threads: threads.map((t) =>
                    t.id === payload.id ? { ...t, created_at: payload.schedule } : t,
                ),
            });
        } else if (event === 'scheduled_message:created') {
            if (payload && payload.id && !threads.some((t) => t.id === payload.id)) {
                const belongsToContact = !payload.conversation
                    || !contactId
                    || payload.conversation.contact_id === contactId;
                if (belongsToContact) {
                    const next = _.sortBy([...threads, payload], (t) => new Date(t.created_at).getTime());
                    set({ threads: next });
                }
            }
        }
    },
}));

export default useContactProfileStore;
